import { canonicalStaffDisplayName } from "../../shared/staffIdentity.js";
// ============================================================
// Google Cloud Identity Auth Utilities
// ============================================================
// Manages JWT token lifecycle, user profile cache, and role cache.
// All sensitive data stored with appropriate scope:
//   - user profile  -> sessionStorage
//   - roles         -> sessionStorage
//
// The backend (Cloud Run) is the source of truth for roles.
// This module provides helpers to sync + cache them locally.
// ============================================================

import type { UserProfile, UserRole } from "./api";

const PROFILE_KEY = "gc_user_profile";
const ROLES_KEY = "gc_user_roles";
const LOGOUT_BROADCAST_KEY = "portal_session_logout";
const ROLE_ALIASES: Record<string, string> = {
  clinical: "clinician",
  parent: "parent_guardian",
  guardian: "parent_guardian",
};
const KNOWN_ROLES = new Set(["superadmin", "admin", "clinician", "supervisor", "staff", "client", "parent_guardian", "marketing"]);

export function normalizeRoleName(roleName: string): string | null {
  const normalized = roleName.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const canonical = ROLE_ALIASES[normalized] ?? normalized;
  return KNOWN_ROLES.has(canonical) ? canonical : null;
}

export function getStoredProfile(): UserProfile | null {
  try {
    const raw = sessionStorage.getItem(PROFILE_KEY);
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

export function setStoredProfile(profile: UserProfile): void {
  sessionStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function clearStoredProfile(): void {
  sessionStorage.removeItem(PROFILE_KEY);
}

export function getStoredRoles(): UserRole[] {
  try {
    const raw = sessionStorage.getItem(ROLES_KEY);
    const parsed = raw ? (JSON.parse(raw) as UserRole[]) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((role) => {
      const name = typeof role?.name === "string" ? normalizeRoleName(role.name) : null;
      return name ? [{ ...role, name }] : [];
    });
  } catch {
    return [];
  }
}

export function setStoredRoles(roles: UserRole[]): void {
  const safeRoles = roles.flatMap((role) => {
    const name = normalizeRoleName(role.name);
    return name ? [{ ...role, name }] : [];
  });
  const serialized = JSON.stringify(safeRoles);
  const changed = sessionStorage.getItem(ROLES_KEY) !== serialized;
  sessionStorage.setItem(ROLES_KEY, serialized);
  if (changed) window.dispatchEvent(new CustomEvent("portalRolesChanged"));
}

export function clearStoredRoles(): void {
  sessionStorage.removeItem(ROLES_KEY);
}

export function hasRole(roleName: string): boolean {
  const normalized = normalizeRoleName(roleName);
  return normalized !== null && getStoredRoles().some((r) => normalizeRoleName(r.name) === normalized);
}

export function hasAnyRole(roleNames: string[]): boolean {
  const normalized = roleNames.map(normalizeRoleName).filter((role): role is string => role !== null);
  return getStoredRoles().some((r) => normalized.includes(r.name));
}

export function getRoleNames(): string[] {
  return getStoredRoles().map((r) => r.name);
}

export function hasPermission(permission: string): boolean {
  if (!permission) return false;
  return isSuperAdmin() || getStoredRoles().some((r) => Array.isArray(r.permissions) && r.permissions.includes(permission));
}

export function hasAnyPermission(permissions: string[]): boolean {
  if (permissions.length > 0 && isSuperAdmin()) return true;
  return getStoredRoles().some((r) =>
    r.permissions.some((p) => permissions.includes(p))
  );
}

// --- Role-based helpers aligned with business requirements ---

export function isClient(): boolean {
  return hasRole("client");
}

export function isParentGuardian(): boolean {
  return hasRole("parent_guardian");
}

/** Pure clinician role — does NOT auto-include superadmin.
 *  Use hasClinicalAccess() for access-gating. */
export function isClinician(): boolean {
  return hasRole("clinician");
}

/** Supervisors are treated as clinicians with additional tools.
 *  Use hasClinicalAccess() for access-gating. */
export function isSupervisor(): boolean {
  return hasRole("supervisor");
}

export function isSuperAdmin(): boolean {
  return hasRole("superadmin");
}

export function isAdmin(): boolean {
  return hasRole("admin");
}

export function isMarketing(): boolean {
  return hasRole("marketing");
}

// --- Composite access helpers ---
// These are the functions used by route guards and the nav.
// SuperAdmin passes all of them.

/** Admin portal access: admin role OR SuperAdmin */
export function hasAdminAccess(): boolean {
  return isAdmin() || isSuperAdmin();
}

/** Staff/clinical portal access: clinician OR supervisor OR SuperAdmin */
export function hasClinicalAccess(): boolean {
  return isClinician() || hasRole("clinical") || isSupervisor() || isSuperAdmin();
}

/** Any employee portal role. This does not imply access to clients or clinical tools. */
export function hasStaffAccess(): boolean {
  return hasAdminAccess() || hasClinicalAccess() || hasRole("staff");
}

/** Supervision tools access: supervisor OR SuperAdmin */
export function canAccessSupervisorDashboard(): boolean {
  return isSupervisor() || isSuperAdmin();
}

/** Marketing portal access: marketing role OR SuperAdmin */
export function hasMarketingAccess(): boolean {
  return isMarketing() || isSuperAdmin();
}

export function canAccessMarketingDashboard(): boolean {
  return hasMarketingAccess();
}

/** Role management: superadmin only */
export function canAssignAdminRole(): boolean {
  return isSuperAdmin();
}

export function canAccessClientData(): boolean {
  return isClient() || isParentGuardian();
}

/**
 * Can this user see dollar billing amounts?
 * Clinicians (staff) cannot by default. Admin, SuperAdmin, and clients can.
 * A future "billing-access" permission can be checked here.
 */
export function canSeeBillingAmounts(): boolean {
  return hasAdminAccess() || isClient() || isSuperAdmin();
}

/**
 * Can this user see psychotherapy notes, treatment plans, diagnoses,
 * clinical measures, and clinical chart content?
 * Admin cannot unless also a clinician or SuperAdmin.
 */
export function canSeeClinicalContent(): boolean {
  return isClinician() || hasRole("clinical") || isSupervisor() || isSuperAdmin();
}

/**
 * Clinicians see only their own assigned clients.
 * Supervisors see their own caseload + supervisee groupings.
 * Admins see all administrative records (not clinical content).
 * SuperAdmin sees everything.
 */
export function canViewOwnClientsOnly(): boolean {
  return (isClinician() || hasRole("clinical")) && !isSuperAdmin();
}

export function canViewSuperviseeClients(): boolean {
  return isSupervisor() || isSuperAdmin();
}

// --- Logout helper ---
export function performLogout(): void {
  clearStoredProfile();
  clearStoredRoles();
  sessionStorage.removeItem("portal_booking_summary");
  sessionStorage.setItem("portal_logged_out_at", String(Date.now()));
}

export function broadcastPortalLogout(): void {
  localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
}

export function clearPortalAuthAndCachedState(): void {
  performLogout();
  for (const storage of [sessionStorage, localStorage]) {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key && (key.startsWith("portal_") || key.startsWith("gc_"))) storage.removeItem(key);
    }
  }
}

// --- Intake helpers from profile ---
export function isNewClient(): boolean {
  const p = getStoredProfile();
  return p?.is_new_client ?? false;
}

export function intakeIsComplete(): boolean {
  const p = getStoredProfile();
  return p?.intake_complete === true;
}

/** @deprecated Intake completion is server-derived; retained for import compatibility. */
export function markIntakeComplete(): void {
  // Deliberately no-op. A cached browser profile cannot unlock the portal.
}

export function areIdentityFieldsLocked(): boolean {
  const p = getStoredProfile();
  return p?.identity_locked ?? false;
}

export function getUserId(): string | null {
  return getStoredProfile()?.id ?? null;
}

export function getUserEmail(): string | null {
  return getStoredProfile()?.email ?? null;
}

export function getUserDisplayName(): string {
  const p = getStoredProfile();
  if (!p) return "";
  const staffRoles=getStoredRoles().filter(role=>["superadmin","admin","clinician","supervisor","staff","marketing"].includes(role.name));
  if (p.staff_display_name || staffRoles.length) return canonicalStaffDisplayName({...p,display_name:p.staff_display_name || p.preferred_name})
    || (staffRoles.some(role=>role.name==="superadmin")?"SuperAdmin":"Staff member");
  if (p.preferred_name) return p.preferred_name;
  return `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
}

export function isLoggedIn(): boolean {
  return getStoredRoles().length > 0;
}

export function isEmailVerified(): boolean { return Boolean(getStoredProfile()?.email_verified_at); }

// Only same-origin portal paths may survive authentication. Route guards still
// evaluate current server roles before the destination mounts.
export function portalReturnPath(search: string, state?: unknown): string | null {
  const value = new URLSearchParams(search).get("next");
  if (!value || [...value].some(character => character.charCodeAt(0) <= 32 || character === "\\") || !/^\/(admin|staff|clientportal)(?:\/|\?|$)/.test(value)) return null;
  const intent = state && typeof state === "object" ? (state as { portalReturn?: { path?: string; createdAt?: number } }).portalReturn : undefined;
  if (intent) {
    const createdAt = intent.createdAt || 0;
    const loggedOutAt = Number(sessionStorage.getItem("portal_logged_out_at") || 0);
    if (intent.path !== value || createdAt <= loggedOutAt || Date.now() - createdAt > 15 * 60_000 || createdAt > Date.now()) return null;
  }
  return value;
}
